import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import { MapNotePinColorMenu } from "@/components/MapNotePinColorMenu";
import { NoteRichPreview, noteContentIsEmpty } from "@/components/notes/NoteRichPreview";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useToast } from "@/components/ui/ToastProvider";
import { cn } from "@/lib/classNames";
import { useRootEntryQuery } from "@/lib/queries/mapNoteEntryQueries";
import {
  useCreateMapNoteMutation,
  useDeleteMapNoteMutation,
  useUpdateMapNoteMutation,
} from "@/lib/queries/mapNoteQueries";
import { useEditorStore } from "@/state/useEditorStore";
import { useMapNoteStore } from "@/state/useMapNoteStore";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { MapNote, MapNotePinColor } from "@west-marches/shared";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from "react";

const NOTE_PREVIEW_MAX_BLOCKS = 6;

const SURFACE_CLASS = cn(
  "relative overflow-hidden rounded-[28px] text-neutral-950 shadow-none",
  MONOLITH_BOARD_STYLE.panelClassName,
);
const ACCENT_CLASS =
  "pointer-events-none absolute inset-x-0 top-0 h-3 bg-[linear-gradient(90deg,#23443a_0%,#d8b35f_50%,#9c4c32_100%)]";
const TITLE_CLASS = "text-neutral-950";
const BODY_CLASS = "text-neutral-800";
const MUTED_CLASS = "text-neutral-600";
const DIVIDER_CLASS = "border-neutral-950/12";
const TITLE_INPUT_CLASS = cn(
  "w-full bg-transparent p-0 m-0 border-0 outline-none focus:outline-none",
  "text-[30px] leading-none font-['Fondamento','Palatino_Linotype',serif]",
  TITLE_CLASS,
  "placeholder:text-neutral-500/70",
);
const POPOVER_ICON_BUTTON_CLASS = cn(
  "inline-flex h-8 w-8 items-center justify-center rounded-md border border-neutral-950/15 bg-[#fffaf1] p-1",
  "transition-colors hover:border-neutral-950/40",
);
const SIDE_PANEL_CLASS = cn(
  "flex flex-row items-center gap-2 rounded-2xl border-[3px] border-neutral-950 bg-[#fffaf1] p-2",
  "shadow-[6px_6px_0_rgba(19,19,19,0.18)]",
);

interface MapNotePopoverProps {
  campaignId: string | null;
  canEdit: boolean;
  mapNoteByHexKey: Map<string, MapNote>;
}

function EyeIcon({ crossedOut }: { crossedOut: boolean }) {
  return crossedOut ? (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    </svg>
  );
}

function NotePromptIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9" />
      <path d="M14 3v6h6" />
      <path d="M9 13h4" />
      <path d="M9 17h6" />
    </svg>
  );
}

function MoveIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 9 2 12l3 3" />
      <path d="M9 5l3-3 3 3" />
      <path d="M15 19l-3 3-3-3" />
      <path d="M19 9l3 3-3 3" />
      <path d="M2 12h20" />
      <path d="M12 2v20" />
    </svg>
  );
}

function PenHintIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

export function MapNotePopover({ campaignId, canEdit, mapNoteByHexKey }: MapNotePopoverProps) {
  const navigate = useNavigate();
  const { shortId } = useParams({ strict: false });
  const hoveredMapNoteId = useMapNoteStore((s) => s.hoveredMapNoteId);
  const editingMapNoteId = useMapNoteStore((s) => s.editingMapNoteId);
  const screenX = useMapNoteStore((s) => s.popoverScreenX);
  const screenY = useMapNoteStore((s) => s.popoverScreenY);
  const stopEditing = useMapNoteStore((s) => s.stopEditing);
  const hidePopover = useMapNoteStore((s) => s.hidePopover);
  const scheduleHide = useMapNoteStore((s) => s.scheduleHide);
  const cancelHide = useMapNoteStore((s) => s.cancelHide);
  const startEditing = useMapNoteStore((s) => s.startEditing);
  const startMoving = useMapNoteStore((s) => s.startMoving);

  const toast = useToast();
  const createMapNoteMutation = useCreateMapNoteMutation(campaignId);
  const updateMapNoteMutation = useUpdateMapNoteMutation(campaignId);
  const deleteMapNoteMutation = useDeleteMapNoteMutation(campaignId);

  const [editName, setEditName] = useState("");
  // Track the map note targeted for deletion separately from `activeMapNote`.
  // The hover popover may hide itself (and unmount its JSX subtree) the moment
  // the confirmation modal layers on top of the cursor — browsers re-evaluate
  // hover under the new top element and fire `mouseleave` on the popover
  // without any mouse movement. Keeping this state and rendering the dialog
  // outside the popover's null-guarded subtree ensures it stays mounted.
  const [noteToDelete, setNoteToDelete] = useState<MapNote | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const initialNameRef = useRef("");
  const skipCommitRef = useRef(false);

  const activeMapNoteId = editingMapNoteId ?? hoveredMapNoteId;
  const activeMapNote = useMemo(
    () =>
      activeMapNoteId
        ? Array.from(mapNoteByHexKey.values()).find((mapNote) => mapNote.id === activeMapNoteId) ??
          null
        : null,
    [activeMapNoteId, mapNoteByHexKey],
  );

  const isEditing = editingMapNoteId !== null;
  const activeMapNoteEntryId = activeMapNote?.id ?? null;
  const isPreviewingAsPlayer = useEditorStore((s) => s.viewAs) === "player";
  const {
    data: notePreview,
    isPending: isNotePreviewPending,
    isError: isNotePreviewError,
  } = useRootEntryQuery(campaignId, activeMapNoteEntryId, "player", {
    asPlayer: isPreviewingAsPlayer,
  });

  useEffect(() => {
    if (isEditing && activeMapNote) {
      setEditName(activeMapNote.name);
      initialNameRef.current = activeMapNote.name;
      skipCommitRef.current = false;
      setTimeout(() => {
        const el = nameInputRef.current;
        if (!el) return;
        el.focus();
        el.select();
      }, 0);
    }
  }, [isEditing, activeMapNote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleCreateClick(e: Event) {
      const detail = (e as CustomEvent).detail as {
        q: number;
        r: number;
        screenX: number;
        screenY: number;
        color?: MapNotePinColor;
        hidden?: boolean;
        offsetX?: number;
        offsetY?: number;
        templateId?: string;
      };
      if (!campaignId || !canEdit) return;
      createMapNoteMutation.mutate(
        {
          q: detail.q,
          r: detail.r,
          name: "",
          ...(detail.color !== undefined ? { color: detail.color } : {}),
          ...(detail.hidden !== undefined ? { hidden: detail.hidden } : {}),
          ...(detail.offsetX !== undefined ? { offsetX: detail.offsetX } : {}),
          ...(detail.offsetY !== undefined ? { offsetY: detail.offsetY } : {}),
          ...(detail.templateId ? { templateId: detail.templateId } : {}),
        },
        {
          onSuccess(created) {
            startEditing(created.id, detail.screenX, detail.screenY);
          },
          onError(error) {
            const message = error instanceof Error ? error.message : "Unknown error";
            if (message.includes("already exists")) {
              toast.error("There's already a map note on this tile.");
            } else {
              toast.error(`Couldn't place pin: ${message}`);
            }
          },
        },
      );
    }
    document.addEventListener("map-note-create-click", handleCreateClick);
    return () => document.removeEventListener("map-note-create-click", handleCreateClick);
  }, [campaignId, canEdit, createMapNoteMutation, startEditing, toast]);

  const commitName = useCallback(() => {
    if (!activeMapNote || !campaignId) {
      stopEditing();
      return;
    }
    const trimmed = editName.trim();
    if (trimmed === initialNameRef.current.trim()) {
      stopEditing();
      return;
    }
    updateMapNoteMutation.mutate(
      { mapNoteId: activeMapNote.id, input: { name: trimmed } },
      { onSuccess: () => stopEditing(), onError: () => stopEditing() },
    );
  }, [activeMapNote, campaignId, editName, updateMapNoteMutation, stopEditing]);

  const handleNameKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        skipCommitRef.current = true;
        commitName();
      } else if (event.key === "Escape") {
        event.preventDefault();
        skipCommitRef.current = true;
        stopEditing();
      }
    },
    [commitName, stopEditing],
  );

  const handleNameBlur = useCallback(() => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      return;
    }
    commitName();
  }, [commitName]);

  const handleToggleVisibility = useCallback(() => {
    if (!activeMapNote || !campaignId) return;
    updateMapNoteMutation.mutate({
      mapNoteId: activeMapNote.id,
      input: { visibleToPlayers: !activeMapNote.visibleToPlayers },
    });
  }, [activeMapNote, campaignId, updateMapNoteMutation]);

  const handleChangePinColor = useCallback(
    (nextColor: MapNotePinColor) => {
      if (!activeMapNote || !campaignId) return;
      updateMapNoteMutation.mutate({
        mapNoteId: activeMapNote.id,
        input: { color: nextColor },
      });
    },
    [activeMapNote, campaignId, updateMapNoteMutation],
  );

  const handleChangeHidden = useCallback(
    (nextHidden: boolean) => {
      if (!activeMapNote || !campaignId) return;
      updateMapNoteMutation.mutate({
        mapNoteId: activeMapNote.id,
        input: { hidden: nextHidden },
      });
    },
    [activeMapNote, campaignId, updateMapNoteMutation],
  );

  const handleRequestDelete = useCallback(() => {
    if (!activeMapNote || !campaignId) return;
    skipCommitRef.current = true;
    setNoteToDelete(activeMapNote);
  }, [activeMapNote, campaignId]);

  const handleConfirmDelete = useCallback(() => {
    if (!noteToDelete || !campaignId) return;
    deleteMapNoteMutation.mutate(noteToDelete.id, {
      onSuccess: () => {
        setNoteToDelete(null);
        stopEditing();
      },
    });
  }, [noteToDelete, campaignId, deleteMapNoteMutation, stopEditing]);

  const handleCancelDelete = useCallback(() => {
    setNoteToDelete(null);
  }, []);

  const confirmDeleteDialog = noteToDelete ? (
    <ConfirmDialog
      open
      title="Delete this map note?"
      description={
        noteToDelete.name
          ? `"${noteToDelete.name}" and all of its notes will be permanently removed. This cannot be undone.`
          : "This pin and all of its notes will be permanently removed. This cannot be undone."
      }
      confirmLabel="Delete"
      cancelLabel="Cancel"
      destructive
      isPending={deleteMapNoteMutation.isPending}
      onConfirm={handleConfirmDelete}
      onCancel={handleCancelDelete}
    />
  ) : null;

  if (!activeMapNote && !activeMapNoteId) return confirmDeleteDialog;
  if (!activeMapNote) return confirmDeleteDialog;

  const handleOpenDetail = () => {
    hidePopover();
    if (!shortId) return;
    void navigate({
      to: "/campaign/$shortId/map-note/$mapNoteId",
      params: { shortId, mapNoteId: activeMapNote.id },
    });
  };

  const handleSurfaceClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (isEditing) return;
    if (event.defaultPrevented) return;
    const target = event.target as HTMLElement | null;
    if (target && target.closest("[data-map-note-popover-stop-click='true']")) {
      return;
    }
    handleOpenDetail();
  };

  const handleSurfaceKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (isEditing) return;
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOpenDetail();
    }
  };

  const hasNoteContent = !noteContentIsEmpty(notePreview?.content);
  const isEmptyNotePrompt = !isNotePreviewPending && !isNotePreviewError && !hasNoteContent;
  const showNoteSection = hasNoteContent || !isEmptyNotePrompt || canEdit;
  const fallbackNoteMessage = isNotePreviewPending
    ? "Loading notes..."
    : isNotePreviewError
    ? "Notes are unavailable right now."
    : "Add player notes to display them here.";
  const visibilityLabel = activeMapNote.visibleToPlayers ? "Hide from players" : "Show to players";

  return (
    <div className="pointer-events-none fixed inset-0 z-30">
      <div
        className="pointer-events-auto absolute"
        style={{
          left: screenX,
          top: screenY,
          transform: "translate(-50%, -100%) translateY(-16px)",
        }}
        onMouseEnter={() => cancelHide()}
        onMouseLeave={() => {
          // Suppress hide-on-leave while a confirm dialog is open. Opening the
          // modal can trigger `mouseleave` here even with no mouse movement
          // because the modal portal layers above the popover and the browser
          // re-evaluates the hover target — we don't want that to tear the
          // popover (and the dialog rendered as its sibling) back down.
          if (!isEditing && !noteToDelete) scheduleHide();
        }}
      >
        <div className="relative">
          <div
            className={cn(
              "min-w-[280px] max-w-[360px] p-5 sm:p-6",
              SURFACE_CLASS,
              !isEditing && "cursor-pointer",
            )}
            role={!isEditing ? "button" : undefined}
            tabIndex={!isEditing ? 0 : undefined}
            aria-label={!isEditing ? "Open map note details" : undefined}
            onClick={!isEditing ? handleSurfaceClick : undefined}
            onKeyDown={!isEditing ? handleSurfaceKeyDown : undefined}
          >
            <div className={ACCENT_CLASS} />
            <div className="relative pt-4p">
              <div className="min-w-0">
                {isEditing ? (
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleNameBlur}
                    onKeyDown={handleNameKeyDown}
                    placeholder="Untitled location"
                    aria-label="Map note name"
                    className={TITLE_INPUT_CLASS}
                  />
                ) : (
                  <h3
                    className={cn(
                      "text-[30px] leading-none font-['Fondamento','Palatino_Linotype',serif]",
                      TITLE_CLASS,
                    )}
                  >
                    {activeMapNote.name || (
                      <span className={MUTED_CLASS}>Untitled location</span>
                    )}
                  </h3>
                )}
              </div>

              {showNoteSection ? (
                <div className={cn("mt-4 border-t-2 pt-4", DIVIDER_CLASS)}>
                  {hasNoteContent ? (
                    <NoteRichPreview
                      content={notePreview?.content}
                      maxBlocks={NOTE_PREVIEW_MAX_BLOCKS}
                      className={cn(
                        "max-h-48 overflow-hidden text-[15px] leading-relaxed font-['Segoe_UI',sans-serif]",
                        "mask-[linear-gradient(to_bottom,black_70%,transparent_100%)]",
                        BODY_CLASS,
                      )}
                    />
                  ) : isEmptyNotePrompt ? (
                    <div
                      role="note"
                      className={cn(
                        "flex items-start gap-2.5 rounded-md border border-neutral-950/10 border-l-[3px] border-l-[#d8b35f] bg-[#fff7e6]/70 px-3 py-2.5",
                        "text-[13.5px] leading-snug font-['Segoe_UI',sans-serif] italic",
                        "text-neutral-700",
                      )}
                    >
                      <span className="mt-px shrink-0 text-[#9c6a1f]" aria-hidden="true">
                        <NotePromptIcon />
                      </span>
                      <span>{fallbackNoteMessage}</span>
                    </div>
                  ) : (
                    <p
                      className={cn(
                        "text-[15px] leading-relaxed font-['Segoe_UI',sans-serif]",
                        MUTED_CLASS,
                      )}
                    >
                      {fallbackNoteMessage}
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {canEdit && (
            <>
              {/* Invisible bridge across the gap so hover-out doesn't fire while
                  moving the cursor between the main popover and the side panel. */}
              <div
                aria-hidden="true"
                className="absolute top-full left-0 h-2 w-full"
              />
              <div
                className={cn("absolute top-full left-0 mt-2", SIDE_PANEL_CLASS)}
                data-map-note-popover-stop-click="true"
              >
                {isEditing ? (
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRequestDelete();
                    }}
                    disabled={deleteMapNoteMutation.isPending}
                    aria-label="Delete this map note"
                    title="Delete this map note"
                    className={cn(
                      POPOVER_ICON_BUTTON_CLASS,
                      "text-red-700 hover:border-red-800/30 hover:bg-red-50",
                    )}
                  >
                    <TrashIcon />
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenDetail();
                      }}
                      aria-label="Edit map note details"
                      className={cn(POPOVER_ICON_BUTTON_CLASS, "text-[#23443a]/65")}
                      data-map-note-click-hint="true"
                      title="Edit map note details"
                    >
                      <PenHintIcon />
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        startMoving(activeMapNote.id);
                      }}
                      aria-label="Move this map note"
                      className={cn(POPOVER_ICON_BUTTON_CLASS, TITLE_CLASS)}
                      title="Move this map note"
                    >
                      <MoveIcon />
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleToggleVisibility();
                      }}
                      aria-label={visibilityLabel}
                      className={cn(POPOVER_ICON_BUTTON_CLASS, TITLE_CLASS)}
                      title={visibilityLabel}
                    >
                      <EyeIcon crossedOut={!activeMapNote.visibleToPlayers} />
                    </button>
                    <MapNotePinColorMenu
                      value={activeMapNote.color}
                      onChange={handleChangePinColor}
                      hidden={activeMapNote.hidden}
                      onChangeHidden={handleChangeHidden}
                      disabled={updateMapNoteMutation.isPending}
                      size="sm"
                      className="justify-center"
                    />
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRequestDelete();
                      }}
                      disabled={deleteMapNoteMutation.isPending}
                      aria-label="Delete this map note"
                      className={cn(
                        POPOVER_ICON_BUTTON_CLASS,
                        "text-red-700 hover:border-red-800/30 hover:bg-red-50",
                      )}
                      title="Delete this map note"
                    >
                      <TrashIcon />
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {confirmDeleteDialog}
    </div>
  );
}
